import { FC, useEffect, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  Grid,
  Link,
  Typography,
  Button,
  Card,
  CardContent,
} from "@material-ui/core";
import ChevronRightIcon from "../../icons/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import DataTable from "src/components/dataTable/dataTable";
import { IColumn } from "src/components/dataTable/tableModel";
import ConfigUserGroupType from "./Types/ConfigUserGroup";
import {
  getConfigMenusForUserGroup,
  getConfigMenusList,
  getConfigUserGroupsList,
  insertUpdateConfigUserGroupMenuMapping,
} from "./ConnectionService";
import UserGroupsCreateEditModal from "./UserGroupCreateEditModal";
import ConfigMenuType from "./Types/ConfigMenuType";
import MenuTree from "../MobileUserGroups/MenuTree";
import { Helmet } from "react-helmet-async";

const columns: IColumn[] = [
  {
    field: "ConfigUserGroup",
    headerName: "Config User Group",
    width: "300px",
  },
  {
    field: "IsDisabled",
    headerName: "Is Disabled",
    width: "300px",
    dataType: 'boolean'
  },
];

const getUniqueMenus = (rawMenus, checkedList: string[]) => {
  const uniqueMenus = [];
  const seen = new Set();
  const filteredMenus = rawMenus.filter((menu) =>
    checkedList.includes(menu.id)
  );

  filteredMenus.forEach((menu) => {
    const uniqueKey = `${menu.ConfigMenuGroupName}-${menu.ConfigMenuName}`;
    if (!seen.has(uniqueKey)) {
      seen.add(uniqueKey);
      uniqueMenus.push(menu);
    }
  });

  return uniqueMenus;
};

function transformMenus(configMenus: ConfigMenuType[]) {
  const sortedMenus = configMenus.sort(
    (a, b) =>
      a.GroupSortOrder - b.GroupSortOrder || a.MenuSortOrder - b.MenuSortOrder
  );

  const transformedMenus = [];
  const flattenedMenus = [];

  sortedMenus.forEach((configMenu) => {
    if (configMenu.ConfigMenuGroupName === null) {
      // If the menu has no group name, it is a top-level menu
      const topMenu = {
        id: `group-${configMenu.GroupSortOrder}`,
        name: configMenu.ConfigMenuName,
        address: configMenu.ConfigMenuURL,
        sortOrder: configMenu.GroupSortOrder,
        submenus: null,
      };
      transformedMenus.push(topMenu);
      flattenedMenus.push({ ...configMenu, id: topMenu.id });
    } else {
      // If the menu has a group name, find or create the group menu
      let groupMenu = transformedMenus.find(
        (menu) => menu.name === configMenu.ConfigMenuGroupName
      );
      if (!groupMenu) {
        groupMenu = {
          id: `group-${configMenu.GroupSortOrder}`,
          name: configMenu.ConfigMenuGroupName,
          address: configMenu.ConfigMenuGroupName,
          sortOrder: configMenu.GroupSortOrder,
          submenus: [],
        };
        transformedMenus.push(groupMenu);
        flattenedMenus.push({ ...configMenu, id: groupMenu.id });
      }
      const subMenu = {
        id: `menu-${configMenu.GroupSortOrder}-${configMenu.MenuSortOrder}`,
        name: configMenu.ConfigMenuName,
        address: configMenu.ConfigMenuURL,
        sortOrder: configMenu.MenuSortOrder,
      };
      groupMenu.submenus?.push(subMenu);
      flattenedMenus.push({ ...configMenu, id: subMenu.id });
    }
  });

  return { transformedMenus, flattenedMenus };
}

const UserGroupsSummary: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [configUserGroupsData, setConfigUserGroupsData] = useState<ConfigUserGroupType[]>([]);
  const [selectedConfigUserGroupKey, setSelectedConfigUserGroupKey] = useState<string | null>(null);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [isMenuLoading, setIsMenuLoading] = useState<boolean>(false);
  const [selectedConfigUserGroupName, setSelectedConfigUserGroupName] =useState<string | null>("");
  const [focusedConfigUserGroupKey, setFocusedConfigUserGroupKey] = useState<string | null>(null);
  const [isTreeSubmitting, setIsTreeSubmitting] = useState<boolean>(false);
  const [configMenus, setConfigMenus] = useState([]);
  const [rawMenus, setRawMenus] = useState([]);

  const fetchConfigUserGroupData = async () => {
    setIsLoading(true);
    try {
      const res = await getConfigUserGroupsList();
      setConfigUserGroupsData(res);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConfigMenus = async () => {
    setIsMenuLoading(true);
    try {
      const res = await getConfigMenusList();
      const transformedResults = transformMenus(res);
      setConfigMenus(transformedResults.transformedMenus);
      setRawMenus(transformedResults.flattenedMenus);
    } finally {
      setIsMenuLoading(false);
    }
  };

  const handleEditClick = (configUserGroupData) => {
    setSelectedConfigUserGroupKey(configUserGroupData.ConfigUserGroupKey);
    setIsModalOpen(true);
  };

  const handleCreateNew = () => {
    setSelectedConfigUserGroupKey(null);
    setIsModalOpen(true);
  };

  const handleCancelClick = () => {
    setCheckedList([]);
  };

  const handleSaveClick = async () => {
    setIsTreeSubmitting(true);

    const filteredSubMenus = checkedList.flatMap((x) => {
      // Find the menu or submenu based on the checkedList
      const configMenu = configMenus.find((y) => y.id === x);

      // If it's a direct match for a group (menu without submenus)
      if (configMenu && (!configMenu.submenus || configMenu.submenus.length === 0)) {
        return [configMenu.id];
      }

      // If the checked item is a submenu
      if (!configMenu) {
        // Find the parent menu of the submenu
        const parentMenu = configMenus.find((menu) =>
          menu.submenus && menu.submenus.some((submenu) => submenu.id === x)
        );

        // If a parent menu is found, and the submenu is checked, return its ID
        if (parentMenu) {
          return parentMenu.submenus
            .filter((submenu) => checkedList.includes(submenu.id))
            .map((submenu) => submenu.id);
        }
      }

      return [];
    });

    const checkedMenus = getUniqueMenus(rawMenus, filteredSubMenus).map(
      (x) => x.ConfigMenuKey
    );

    const payload = {
      ConfigUserGroupKey: focusedConfigUserGroupKey,
      ConfigMenuKeys: checkedMenus,
    };

    const res = await insertUpdateConfigUserGroupMenuMapping(payload);

    if (res) {
      setFocusedConfigUserGroupKey(null);
      setSelectedConfigUserGroupName("");
      setCheckedList([]);
    }
    setIsTreeSubmitting(false);
  };

  const handleRowClick = (row: any, open: boolean) => {
    if (row && open) {
      setIsMenuLoading(true);
      setSelectedConfigUserGroupName(row.ConfigUserGroup);
      setFocusedConfigUserGroupKey(row.ConfigUserGroupKey);
      getConfigMenusForUserGroup(row.ConfigUserGroupKey).then((res) => {
        setCheckedList(
          rawMenus.filter((x) => res.includes(x.ConfigMenuKey)).map((x) => x.id)
        );
        setIsMenuLoading(false);
      });
    } else {
      setFocusedConfigUserGroupKey(null);
      setSelectedConfigUserGroupName("");
      setCheckedList([]);
    }
  };

  const handleModalClose = () => {
    fetchConfigUserGroupData();
    setIsModalOpen(false);
    setSelectedConfigUserGroupKey(null);
  };

  useEffect(() => {
    fetchConfigUserGroupData();
    fetchConfigMenus();
  }, []);

  return (
    <>
      <Helmet>
        <title>Config User Groups</title>
      </Helmet>
      <Box>
        <Container maxWidth={false}>
          <Grid alignItems="center" container justifyContent="space-between">
            <Grid item>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
              >
                <Link
                  color="textPrimary"
                  component={RouterLink}
                  to=""
                  variant="h5"
                >
                  Administration
                </Link>
                <Typography color="textPrimary" variant="h5">
                  User Groups List
                </Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item>
              <Box sx={{ my: 1 }}>
                <Button
                  sx={{
                    background: "linear-gradient(to bottom, #072f40, #072f40)",
                    minWidth: 100,
                  }}
                  variant="contained"
                  onClick={handleCreateNew}
                >
                  Create New
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ flex: 1 }}>
            <Grid item xl={8} lg={8} md={12} sm={12} xs={12}>
              <Card sx={{ height: "600px", paddingBottom: "76%" }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Menu
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    Add/Remove menus and submenus for user group
                  </Typography>
                  <DataTable
                    columns={columns}
                    dataList={configUserGroupsData}
                    isLoading={isLoading}
                    onClickEdit={handleEditClick}
                    infoText=""
                    showActionsHeader
                    onRowClick={handleRowClick}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xl={4} lg={4} md={12} sm={12} xs={12}>
              <Card style={{ height: "100%" }}>
                <CardContent
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <span style={{ display: "flex", justifyContent: "flex-end" }}>
                    {checkedList && checkedList.length > 0 && (
                      <Button
                        sx={{
                          minWidth: 100,
                          marginRight: "2%",
                          color: "rgb(107, 119, 140)",
                        }}
                        variant="outlined"
                        size="small"
                        onClick={handleCancelClick}
                      >
                        Clear
                      </Button>
                    )}
                    {focusedConfigUserGroupKey && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveClick}
                        disabled={isTreeSubmitting}
                        sx={{
                          background:
                            "linear-gradient(to bottom, #072f40, #072f40)",
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </span>
                  <span
                    style={{
                      marginTop: "2%",
                      marginBottom: "2%",
                      width: "90%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedConfigUserGroupName || ""}
                  </span>
                  <Box
                    alignItems="center"
                    style={{ overflowY: "auto", height: "100%" }}
                  >
                    <MenuTree
                      isTreeLoading={isMenuLoading}
                      data={configMenus}
                      checkedList={checkedList}
                      setCheckedList={setCheckedList}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <UserGroupsCreateEditModal
        open={isModalOpen}
        handleClose={handleModalClose}
        configUserGroupKey={selectedConfigUserGroupKey}
      />
    </>
  );
};

export default UserGroupsSummary;
